import { useRef, useState } from 'react';
import { Bell, Loader2, Mail, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth.ts';
import { useConfirm } from '@/hooks/useConfirm.tsx';
import { useDivisions } from '@/hooks/useDivisions.ts';
import { useRegions } from '@/hooks/useRegions.ts';
import {
  RULE_CATEGORIES,
  SEVERITIES,
  useCreateNotificationRule,
  useDeleteNotificationRule,
  useNotificationDestinations,
  useNotificationRules,
  useUpdateNotificationRule,
  type NotificationDestination,
  type NotificationRule,
  type RuleChannel,
  type RuleInput,
  type RuleDelivery,
  RULE_DELIVERY_LABELS,
  type RuleSeverity,
} from '@/hooks/useNotifications.ts';
import { SEVERITY_LABELS, ruleSentence } from './ruleSentence.ts';
import { useClauseTargets, type ClauseTargets } from './useClauseTargets.ts';
import { ClauseList } from './ClauseEditor.tsx';
import { usePushDevices } from '@/features/webPush';
import { EDITION } from '@/lib/edition.ts';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Switch } from '@/components/ui/switch.tsx';

/**
 * Rules read as sentences, not as a query builder.
 *
 * That is the whole UI bet (§10). A rule is a conjunction of clauses and rules union
 * with each other, which is disjunctive normal form — so every policy is expressible
 * with no nesting, no parentheses and no AND/OR picker. Top-level OR is "add another
 * rule", which union semantics makes exactly equivalent.
 *
 * Each chip is one clause with two toggles: *affecting ⇄ confined to* (the
 * quantifier) and *is ⇄ is not* (negation). All four combinations are meaningful over
 * an incident that spans several regions — "not confined to the lab" stays quiet
 * about lab-only incidents but pages you when one spreads, which is not expressible
 * any other way.
 */

/** Stable identity so the memoized target lists do not rebuild while a query loads. */
const EMPTY_LIST: { id: string; name: string }[] = [];

const EMPTY_DRAFT: RuleInput = {
  categories: null,
  categoriesNegated: false,
  minSeverity: 'info',
  maxSeverity: null,
  includeResolutions: true,
  channel: 'in_app',
  delivery: 'immediate',
  destinationId: null,
  enabled: true,
  clauses: [],
};

/** The composer. Previews the sentence live, because the sentence *is* the rule. */
function RuleComposer({
  initial,
  targets,
  organizationId,
  emailAvailable,
  pushAvailable,
  emailDestinations,
  busy,
  onCancel,
  onSubmit,
}: {
  initial: RuleInput;
  targets: ClauseTargets;
  organizationId: string | undefined;
  emailAvailable: boolean;
  pushAvailable: boolean;
  /** The caller's own **verified** email destinations, newest last. */
  emailDestinations: NotificationDestination[];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (rule: RuleInput) => void;
}) {
  const [draft, setDraft] = useState<RuleInput>(initial);
  const { scopes, tagDefinitions, tagValueLabels } = targets;

  const preview = ruleSentence(
    { ...draft, id: '', organizationId: '', seeded: false, createdAt: '' },
    scopes,
    emailDestinations.find((d) => d.id === draft.destinationId)?.target,
    tagValueLabels,
  );

  const toggleCategory = (key: string) => {
    setDraft((d) => {
      // `null` means "every category, including ones that do not exist yet" — the
      // storage keeps that meaning, and the checkboxes show it as all-ticked. So the
      // first uncheck has to materialise the full list before removing from it,
      // otherwise it would read as unchecking one box and get stored as checking one.
      const current = d.categories ?? RULE_CATEGORIES.map((c) => c.key);
      const next = current.includes(key) ? current.filter((c) => c !== key) : [...current, key];
      // Back to all-ticked collapses to `null` rather than freezing today's list. An
      // explicit list of every category is NOT the same claim: add a ninth category and
      // a frozen list silently stops covering it, which is how a firehose rule quietly
      // develops a blind spot.
      return { ...d, categories: next.length === RULE_CATEGORIES.length ? null : next };
    });
  };

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <p className="text-sm font-medium">{preview}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Send me</Label>
          <Select
            value={draft.channel}
            onValueChange={(v) => {
              setDraft((d) => ({
                ...d,
                channel: v as RuleChannel,
                delivery: v === 'email' ? d.delivery : 'immediate',
                // Same reason as `delivery`: a control that is not on screen must not
                // keep a value. A push rule carrying an email destinationId would be
                // silently wrong rather than visibly wrong.
                destinationId: v === 'email' ? d.destinationId : null,
              }));
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in_app">In-app</SelectItem>
              <SelectItem value="email" disabled={!emailAvailable}>
                Email{emailAvailable ? '' : ' — not configured on this deployment'}
              </SelectItem>
              <SelectItem value="push" disabled={!pushAvailable}>
                {/* Two different reasons a channel is unavailable, and they must not
                    share copy: the trial does not include push (a sales message), while
                    a SaaS deployment missing a VAPID key is unconfigured (an operator
                    message). "Requires HTTPS"-shaped copy in the trial reads as the user
                    having broken something — see §6. */}
                Push
                {pushAvailable ? '' : EDITION === 'trial' ? ' — available in the full product' : ' — not configured on this deployment'}
              </SelectItem>
            </SelectContent>
          </Select>
          {/* Digests are email-only (Q10). The inbox IS the accumulation so it is
              always immediate (Q7), and a digest push is the thing people disable push
              over — so the selector appears for email and is absent, rather than shown
              and ignored, everywhere else. Switching channel away from email resets the
              rule to immediate so a hidden control can never hold a value the backend
              will reject. */}
          {/* Which address. Absent when the account has only its own identity email,
              because a picker with one option is a control that teaches nothing — the
              sentence below already says where it goes. `null` means "wherever my
              account email is", which is what the router resolves when a rule names
              no destination. */}
          {draft.channel === 'email' && emailDestinations.length > 0 ? (
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs">To</Label>
              <Select
                value={draft.destinationId ?? 'default'}
                onValueChange={(v) => {
                  setDraft((d) => ({ ...d, destinationId: v === 'default' ? null : v }));
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">My account email</SelectItem>
                  {emailDestinations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.target}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {draft.channel === 'email' ? (
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs">Send</Label>
              <Select
                value={draft.delivery}
                onValueChange={(v) => { setDraft((d) => ({ ...d, delivery: v as RuleDelivery })); }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(RULE_DELIVERY_LABELS) as RuleDelivery[]).map((d) => (
                    <SelectItem key={d} value={d}>
                      {RULE_DELIVERY_LABELS[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {draft.delivery !== 'immediate' ? (
                <p className="text-xs text-muted-foreground">
                  Held and sent as one email. Nothing is sent when nothing happens.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Severity</Label>
          <div className="flex items-center gap-2">
            <Select value={draft.minSeverity} onValueChange={(v) => { setDraft((d) => ({ ...d, minSeverity: v as RuleSeverity })); }}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SEVERITY_LABELS[s]} and above
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={draft.maxSeverity ?? '__none__'}
              onValueChange={(v) => { setDraft((d) => ({ ...d, maxSeverity: v === '__none__' ? null : (v as RuleSeverity) })); }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">no upper limit</SelectItem>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>
                    up to {SEVERITY_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">About</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {RULE_CATEGORIES.map((c) => (
            <label key={c.key} className="flex items-center gap-1.5 text-xs" title={c.description}>
              <Checkbox
                checked={draft.categories?.includes(c.key) ?? true}
                // The last remaining category cannot be unticked. Allowing it would
                // need a "no categories" state, and the only two candidate meanings
                // are "match nothing" (a rule that exists to do nothing) and "match
                // everything" (the inversion this change removed). Refusing the click
                // is the honest third option.
                disabled={draft.categories?.length === 1 && draft.categories[0] === c.key}
                onCheckedChange={() => { toggleCategory(c.key); }}
              />
              {c.label}
            </label>
          ))}
        </div>
        {/* No hint about "empty means everything" any more, because it no longer does.
            All-ticked is the default and it is shown as all-ticked; the last box cannot
            be unticked, because a rule about no categories is a rule about nothing. */}
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Where</Label>
        <ClauseList
          clauses={draft.clauses}
          scopes={scopes}
          organizationId={organizationId}
          tagDefinitions={tagDefinitions}
          tagValueLabels={tagValueLabels}
          onChange={(next) => { setDraft((d) => ({ ...d, clauses: next })); }}
        />
        <p className="text-xs text-muted-foreground">
          No conditions means the whole organization. Conditions combine with “and” — for “or”, add a second rule.
        </p>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <Switch
          checked={draft.includeResolutions}
          onCheckedChange={(v) => { setDraft((d) => ({ ...d, includeResolutions: v })); }}
        />
        Tell me when these resolve, too
      </label>

      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => {
            // The picker is hidden both when the channel is not email AND when the
            // account has no verified addresses to choose from. Switching channel
            // clears the field, but losing the last verified destination does not —
            // so normalise here, where every path to Save passes, rather than relying
            // on each condition to clean up after itself.
            onSubmit(draft.channel === 'email' && emailDestinations.length > 0 ? draft : { ...draft, destinationId: null });
          }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Save rule
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * `namedRuleId` is a rule to pick out and scroll to: the one a notification email
 * named, when its reader followed the email's link here.
 */
export function NotificationRules({ namedRuleId = null }: { namedRuleId?: string | null } = {}) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? undefined;
  const { data, isLoading } = useNotificationRules();
  const { data: regions } = useRegions(organizationId);
  const { data: divisions } = useDivisions(organizationId);
  const create = useCreateNotificationRule();
  const update = useUpdateNotificationRule();
  const remove = useDeleteNotificationRule();
  const { confirm, confirmDialog } = useConfirm();

  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rules = data?.rules ?? [];
  // The rule an email named: picked out, and scrolled to once, so the reader lands on
  // the rule that wrote to them.
  const named = namedRuleId;
  const scrolled = useRef(false);
  const targets = useClauseTargets(
    organizationId,
    divisions ?? EMPTY_LIST,
    regions ?? EMPTY_LIST,
    rules.flatMap((r) => r.clauses),
  );
  const { scopes, tagValueLabels } = targets;
  const emailAvailable = data?.emailAvailable ?? true;
  const pushAvailable = data?.pushAvailable ?? false;
  const { data: pushDevices } = usePushDevices();
  // Only verified ones: an unverified destination resolves to `skipped_unverified` on
  // every event, so offering it would let someone point a rule at silence.
  const { data: destinations } = useNotificationDestinations();
  const emailDestinations = (destinations ?? []).filter((d) => d.kind === 'email' && d.verified);
  const destinationLabel = (id: string | null) => (id ? emailDestinations.find((d) => d.id === id)?.target : undefined);
  const needsEnrollment = pushAvailable && rules.some((r) => r.channel === 'push') && (pushDevices?.length ?? 0) === 0;

  const toInput = (rule: NotificationRule): RuleInput => ({
    categories: rule.categories,
    categoriesNegated: rule.categoriesNegated,
    minSeverity: rule.minSeverity,
    maxSeverity: rule.maxSeverity,
    includeResolutions: rule.includeResolutions,
    channel: rule.channel,
    delivery: rule.delivery,
    destinationId: rule.destinationId,
    enabled: rule.enabled,
    clauses: rule.clauses,
  });

  const submit = (rule: RuleInput) => {
    setError(null);
    const onError = (e: unknown) => {
      const message =
        (e as { response?: { data?: { description?: string } } }).response?.data?.description ?? 'Could not save the rule.';
      setError(message);
    };
    if (editingId) {
      update.mutate(
        { id: editingId, rule },
        { onSuccess: () => { setEditingId(null); }, onError },
      );
    } else {
      create.mutate(rule, { onSuccess: () => { setComposing(false); }, onError });
    }
  };

  /**
   * Losing the last in-app rule stops the inbox filling — the kind of self-inflicted
   * silence people spend an afternoon debugging. Deleting and switching off arrive at
   * exactly the same place, so they share one guard.
   *
   * It counts only rules that are **on**. Before the switch existed, "I still have an
   * in-app rule" and "the bell still fills" were the same statement; they are not
   * anymore, and a guard that counted rows would wave through the delete that
   * actually silences you.
   */
  const confirmStillDelivers = async (rule: NotificationRule, verb: 'delete' | 'disable') => {
    const another = rules.some((r) => r.channel === 'in_app' && r.enabled && r.id !== rule.id);
    if (rule.channel !== 'in_app' || !rule.enabled || another) return true;
    return confirm({
      title: 'Stop showing notifications in the app?',
      message:
        'This is the last in-app rule you have switched on. Notifications will still be recorded and you can still see who was notified — but the bell will stop filling.',
      confirmText: verb === 'delete' ? 'Delete rule' : 'Turn it off',
      isDanger: verb === 'delete',
    });
  };

  const handleDelete = async (rule: NotificationRule) => {
    if (!(await confirmStillDelivers(rule, 'delete'))) return;
    remove.mutate(rule.id);
  };

  const handleToggle = async (rule: NotificationRule, enabled: boolean) => {
    if (!enabled && !(await confirmStillDelivers(rule, 'disable'))) return;
    setError(null);
    update.mutate({ id: rule.id, rule: { ...toInput(rule), enabled } });
  };

  return (
    <section className="bg-card border border-border rounded-lg">
      <header className="px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold">Notification rules</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          What reaches you, and where. Each rule is one sentence; rules add up, so for “or” just add another.
        </p>
      </header>

      <div className="p-5 space-y-3">
        {!emailAvailable ? (
          <p className="text-xs text-muted-foreground rounded-md border border-border px-3 py-2">
            Email is not configured on this deployment, so email rules cannot deliver. In-app notifications work as normal.
          </p>
        ) : null}

        {/* The nudge §5.3 asks for, and the reason it is placed here rather than at the
            top of the page: it sits directly above the rule it is about and directly
            above the enrolment card that resolves it.

            It used to fire only for a hand-written push rule, on the reasoning that the
            intent was already expressed. `seed_for_membership` now seeds one for every
            new membership, so this shows for anyone who has not enrolled — which is the
            point rather than a regression: the seeded rule is the reason the router
            plans nothing for push, and this is the only place that says so. The router
            deliberately writes no delivery-log row for an unenrolled account (see the
            push arm of `plan_rule`), so if this line does not explain the silence,
            nothing does.

            None of this touches the auto-quieting hazard, which is about
            `Notification.requestPermission()` — still reachable only from the Enable
            button, on a click, in `PushEnrollment`. This is text. */}
        {needsEnrollment ? (
          <p className="text-xs text-muted-foreground rounded-md border border-border px-3 py-2">
            One of your rules sends push notifications, but no browser is enrolled to receive them. Enable push
            on this browser on the Destinations page.
          </p>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading rules…
          </div>
        ) : rules.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No rules yet — nothing will reach you. Add one to start.
          </p>
        ) : (
          <ul className="space-y-2">
            {rules.map((rule) =>
              editingId === rule.id ? (
                <li key={rule.id}>
                  <RuleComposer
                    initial={toInput(rule)}
                    targets={targets}
                    organizationId={organizationId}
                    emailAvailable={emailAvailable}
                    pushAvailable={pushAvailable}
                    emailDestinations={emailDestinations}
                    busy={update.isPending}
                    onCancel={() => { setEditingId(null); setError(null); }}
                    onSubmit={submit}
                  />
                </li>
              ) : (
                <li
                  key={rule.id}
                  data-named={rule.id === named ? 'true' : undefined}
                  ref={
                    rule.id === named
                      ? (el) => {
                          if (el && !scrolled.current) {
                            scrolled.current = true;
                            el.scrollIntoView({ block: 'center' });
                          }
                        }
                      : undefined
                  }
                  className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${
                    rule.id === named ? 'border-primary ring-1 ring-primary' : 'border-border'
                  }`}
                >
                  <div className="flex items-start gap-2 min-w-0">
                    {rule.channel === 'email' ? (
                      <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <Bell className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                      <p id={`rule-sentence-${rule.id}`} className="text-sm break-words">
                        {ruleSentence(rule, scopes, destinationLabel(rule.destinationId), tagValueLabels)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Turning a rule off is not editing it — you do it for an afternoon
                        and undo it, which is why it sits on the row rather than three
                        clicks into the composer. The composer still writes the same
                        field, so the two cannot disagree. */}
                    {/* Named by the sentence rather than by "this rule": a rule has no
                        name, so the sentence is its identity, and every switch on the
                        page would otherwise announce itself identically. */}
                    <Switch
                      checked={rule.enabled}
                      aria-labelledby={`rule-sentence-${rule.id}`}
                      disabled={update.isPending && update.variables.id === rule.id}
                      onCheckedChange={(enabled) => { void handleToggle(rule, enabled); }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Edit rule"
                      onClick={() => { setComposing(false); setEditingId(rule.id); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Delete rule"
                      disabled={remove.isPending}
                      onClick={() => { void handleDelete(rule); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}

        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        {composing ? (
          <RuleComposer
            initial={EMPTY_DRAFT}
            targets={targets}
            organizationId={organizationId}
            emailAvailable={emailAvailable}
            pushAvailable={pushAvailable}
            emailDestinations={emailDestinations}
            busy={create.isPending}
            onCancel={() => { setComposing(false); setError(null); }}
            onSubmit={submit}
          />
        ) : editingId === null ? (
          <Button variant="outline" size="sm" onClick={() => { setComposing(true); }}>
            <Plus className="w-3.5 h-3.5" /> Add rule
          </Button>
        ) : null}
      </div>
      {confirmDialog}
    </section>
  );
}
